import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { bulkUploadCreditDebitNotes, clearCreditDebitNoteBulkUploadState } from '../../../../redux/actions/SocietyPortal/CreditDebitNoteAction/CreditDebitNoteAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { downloadCreditDebitNoteTemplateReq } from '../../../../axios/request/SocietyPortal/CreditDebitNoteReqs/CreditDebitNoteReqs';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_CREDIT_NOTES, SOCIETY_PORTAL_DEBIT_NOTES } from '../../../../Routing/routes';

const ALLOWED_EXTENSIONS = ['xlsx', 'xls'];

const formatCurrency = (amount) =>
    `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STEPS = [
    { num: '01', label: 'UPLOAD', desc: 'Upload your file' },
    { num: '02', label: 'RESULT', desc: 'Import result' },
];

// One upload handles both kinds - the API, the template and the file are shared, and each
// row's note_type column decides what gets created. noteType only says which list to return to.
const BulkUploadCreditDebitNotes = ({ noteType = 'CREDIT_NOTE' }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const cameFromCreditNotes = noteType === 'CREDIT_NOTE';
    const listLabel = cameFromCreditNotes ? 'Credit Notes' : 'Debit Notes';
    const listRoute = cameFromCreditNotes ? SOCIETY_PORTAL_CREDIT_NOTES : SOCIETY_PORTAL_DEBIT_NOTES;

    const [currentStep, setCurrentStep] = useState(0);
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [invoiceTypeId, setInvoiceTypeId] = useState('');
    const [templateDownloading, setTemplateDownloading] = useState(false);
    const fileInputRef = useRef(null);

    const { bulkUploadLoading, bulkUploadResult } = useSelector(state => state.CreditDebitNoteReducer || {});
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const societyId = selectedSocietyStorer.getSocietyId();

    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        return () => { dispatch(clearCreditDebitNoteBulkUploadState()); };
    }, [dispatch, societyId]);

    const goBack = () => navigate(SOCIETY_PORTAL + listRoute);

    const handleFile = (selectedFile) => {
        if (!selectedFile) return;
        const ext = selectedFile.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            setFileError('Only Excel files (.xls, .xlsx) are supported.');
            setFile(null);
            return;
        }
        setFileError(null);
        setFile(selectedFile);
    };

    const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); };
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleInputChange = (e) => handleFile(e.target.files[0]);
    const handleRemoveFile = () => { setFile(null); setFileError(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

    const formatSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleDownloadTemplate = async () => {
        if (!invoiceTypeId) {
            setFileError('Please select an invoice type first to download the template.');
            return;
        }
        setTemplateDownloading(true);
        try {
            const response = await downloadCreditDebitNoteTemplateReq({
                data: { society_id: societyId, invoice_type_id: invoiceTypeId }
            });
            const blob = response instanceof Blob ? response : new Blob([response], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'credit_debit_note_bulk_upload_template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            setFileError('Failed to download template. Please try again.');
        } finally {
            setTemplateDownloading(false);
        }
    };

    const handleUpload = () => {
        if (!file || !invoiceTypeId) return;
        dispatch(bulkUploadCreditDebitNotes({
            society_id: societyId,
            invoice_type_id: parseInt(invoiceTypeId),
            file,
            onSuccess: () => setCurrentStep(1),
        }));
    };

    const created = bulkUploadResult?.created || [];
    const failed = bulkUploadResult?.failed || [];
    const allSuccess = failed.length === 0;

    return (
        <div className="tw-bg-gray-50 tw-min-h-screen">
            {/* Header Bar */}
            <div className="tw-bg-white tw-border-b tw-border-gray-200 tw-px-5 tw-relative" style={{ height: 60 }}>
                <div className="tw-absolute tw-left-5 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-items-center tw-gap-3">
                    <button onClick={goBack} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div className="tw-leading-tight">
                        <p className="tw-text-[10px] tw-font-semibold tw-text-indigo-600 tw-uppercase tw-tracking-wider tw-m-0">Bulk Upload</p>
                        <h1 className="tw-text-[15px] tw-font-bold tw-text-gray-900 tw-m-0 tw-mt-px">Credit/Debit Excel Upload</h1>
                    </div>
                </div>

                {/* Stepper */}
                <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-flex tw-items-center">
                    {STEPS.map((step, index) => (
                        <div key={step.label} className="tw-flex tw-items-center">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                <div style={{ width: 34, height: 34 }} className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-flex-shrink-0 ${
                                    index < currentStep
                                        ? 'tw-bg-emerald-500 tw-text-white'
                                        : index === currentStep
                                            ? 'tw-bg-indigo-600 tw-text-white'
                                            : 'tw-bg-white tw-text-gray-400 tw-border tw-border-gray-300'
                                }`}>
                                    {index < currentStep ? <Icon icon="mdi:check" className="tw-text-sm" /> : step.num}
                                </div>
                                <div>
                                    <p className={`tw-text-xs tw-font-semibold tw-m-0 ${index <= currentStep ? 'tw-text-gray-700' : 'tw-text-gray-400'}`}>{step.label}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-m-0 tw-font-normal">{step.desc}</p>
                                </div>
                            </div>
                            {index < STEPS.length - 1 && (
                                <div className={`tw-w-28 tw-mx-5 tw-border-t tw-border-dashed ${index < currentStep ? 'tw-border-emerald-400' : 'tw-border-gray-300'}`} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="tw-p-6">
                {/* Step 1: Upload */}
                {currentStep === 0 && (
                    <div>
                        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                            <div className="tw-grid tw-grid-cols-2">
                                {/* Left Side */}
                                <div className="tw-p-10 tw-flex tw-flex-col tw-justify-center tw-border-r tw-border-gray-100">
                                    <p className="tw-text-sm tw-text-gray-400 tw-font-medium">Seamless</p>
                                    <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mt-0.5">Credit/Debit Excel Upload</h2>
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-3 tw-leading-relaxed">
                                        Upload one Excel file for both note types - the note_type column on each row decides
                                        what is created. Tax is auto-calculated from tariff head tax rules. Notes are created as DRAFT.
                                    </p>
                                    <ul className="tw-mt-5 tw-space-y-3">
                                        <li className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">Auto GST calculation from tax rules</span>
                                        </li>
                                        <li className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">Dynamic columns per invoice type</span>
                                        </li>
                                        <li className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">Optional invoice reference linking</span>
                                        </li>
                                    </ul>

                                    <div className="tw-mt-6 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-100">
                                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-2">Fixed Columns</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {['wing_title', 'flat_number', 'note_type', 'issue_date', 'reason'].map(col => (
                                                <span key={col} className="tw-px-2 tw-py-0.5 tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-text-[10px] tw-font-medium tw-text-gray-600">{col}</span>
                                            ))}
                                        </div>
                                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-2 tw-mt-3">Optional Columns</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {['invoice_document_number', 'due_date', 'remarks'].map(col => (
                                                <span key={col} className="tw-px-2 tw-py-0.5 tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-text-[10px] tw-font-medium tw-text-gray-400">{col}</span>
                                            ))}
                                        </div>
                                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-2 tw-mt-3">Note Type</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {['CREDIT_NOTE', 'DEBIT_NOTE'].map(val => (
                                                <span key={val} className="tw-px-2 tw-py-0.5 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded tw-text-[10px] tw-font-medium tw-text-blue-600">{val}</span>
                                            ))}
                                        </div>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-2">
                                            + Dynamic tariff head columns based on selected invoice type
                                        </p>
                                        <div className="tw-mt-3 tw-flex tw-items-start tw-gap-1.5 tw-p-2 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded">
                                            <Icon icon="mdi:information-outline" className="tw-text-amber-500 tw-text-xs tw-mt-px tw-flex-shrink-0" />
                                            <p className="tw-text-[10px] tw-text-amber-700 tw-m-0">
                                                Leave <span className="tw-font-semibold">due_date</span> blank for CREDIT_NOTE rows - a credit
                                                note is not collected, so it carries no due date. It is required for DEBIT_NOTE rows.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side - Upload Area */}
                                <div className="tw-p-10 tw-flex tw-flex-col tw-items-center tw-justify-center">
                                    {/* Invoice Type Selector */}
                                    <div className="tw-w-full tw-mb-5">
                                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5">Invoice Type *</label>
                                        <select
                                            value={invoiceTypeId}
                                            onChange={(e) => setInvoiceTypeId(e.target.value)}
                                            className="tw-w-full tw-px-3 tw-py-2.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                        >
                                            <option value="">Select Invoice Type</option>
                                            {(invoiceTypesData || []).map(t => (
                                                <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onClick={() => fileInputRef.current?.click()}
                                        className={`tw-w-full tw-border-2 tw-border-dashed tw-rounded-xl tw-py-12 tw-px-8 tw-text-center tw-cursor-pointer tw-transition-colors ${
                                            isDragging
                                                ? 'tw-border-indigo-400 tw-bg-indigo-50'
                                                : 'tw-border-gray-200 hover:tw-border-gray-300 tw-bg-gray-50/50'
                                        }`}
                                    >
                                        <div className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                                            <Icon icon="mdi:cloud-upload-outline" className="tw-text-2xl tw-text-indigo-400" />
                                        </div>
                                        <p className="tw-text-sm tw-font-semibold tw-text-gray-700">Drag files to import</p>
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-1">
                                            or <span className="tw-text-blue-500 tw-font-medium hover:tw-underline">browse local files</span>
                                        </p>
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-4">
                                            <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider tw-font-medium">XLS / XLSX</span>
                                            <span className="tw-text-gray-300">&middot;</span>
                                            <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider tw-font-medium">Max 50MB</span>
                                        </div>
                                        <input ref={fileInputRef} type="file" accept=".xls,.xlsx" onChange={handleInputChange} className="tw-hidden" />
                                    </div>

                                    {fileError && (
                                        <div className="tw-w-full tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3">
                                            <Icon icon="mdi:alert-circle" className="tw-text-red-500 tw-text-sm tw-mt-0.5 tw-flex-shrink-0" />
                                            <p className="tw-text-xs tw-text-red-600">{fileError}</p>
                                        </div>
                                    )}

                                    {file && !fileError && (
                                        <div className="tw-w-full tw-mt-3 tw-flex tw-items-center tw-justify-between tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg tw-p-3">
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <Icon icon="mdi:file-excel-outline" className="tw-text-emerald-500 tw-text-lg" />
                                                <div>
                                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">{file.name}</p>
                                                    <p className="tw-text-[10px] tw-text-gray-400">{formatSize(file.size)}</p>
                                                </div>
                                            </div>
                                            <button onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }} className="tw-p-1 tw-rounded hover:tw-bg-emerald-100 tw-text-gray-400 hover:tw-text-red-500">
                                                <Icon icon="mdi:close" className="tw-text-sm" />
                                            </button>
                                        </div>
                                    )}

                                    <button
                                        onClick={handleDownloadTemplate}
                                        disabled={templateDownloading}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-blue-500 tw-text-xs tw-font-medium hover:tw-text-blue-600 tw-mt-4 disabled:tw-opacity-50"
                                    >
                                        <Icon icon={templateDownloading ? "mdi:loading" : "mdi:download-outline"} className={`tw-text-base ${templateDownloading ? 'tw-animate-spin' : ''}`} />
                                        {templateDownloading ? 'Downloading...' : 'Download Template'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-5">
                            <button onClick={goBack} disabled={bulkUploadLoading} className="tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-gray-500 tw-bg-gray-100 hover:tw-bg-gray-200 disabled:tw-opacity-50 tw-transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleUpload} disabled={!file || !invoiceTypeId || !!fileError || bulkUploadLoading} className="tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-white tw-bg-indigo-600 hover:tw-bg-indigo-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors tw-flex tw-items-center tw-gap-1.5">
                                {bulkUploadLoading && <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />}
                                <Icon icon="mdi:upload-outline" className="tw-text-sm" />
                                {bulkUploadLoading ? 'Uploading...' : 'Upload & Import'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Result */}
                {currentStep === 1 && bulkUploadResult && (
                    <div>
                        {/* Result Summary Card */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-6 tw-text-center">
                            <div className={`tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto ${allSuccess ? 'tw-bg-emerald-50' : 'tw-bg-amber-50'}`}>
                                <Icon icon={allSuccess ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'} className={`tw-text-3xl ${allSuccess ? 'tw-text-emerald-500' : 'tw-text-amber-500'}`} />
                            </div>
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mt-3">
                                {allSuccess ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                            </h3>
                            <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                                {allSuccess
                                    ? `All ${created.length} notes have been created as DRAFT.`
                                    : `${created.length} notes created, ${failed.length} had errors.`
                                }
                            </p>
                            <div className="tw-flex tw-items-center tw-justify-center tw-gap-5 tw-mt-5">
                                <div className="tw-text-center">
                                    <p className="tw-text-lg tw-font-semibold tw-text-gray-800">{created.length + failed.length}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Total</p>
                                </div>
                                <div className="tw-w-px tw-h-8 tw-bg-gray-200" />
                                <div className="tw-text-center">
                                    <p className="tw-text-lg tw-font-semibold tw-text-emerald-600">{created.length}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Created</p>
                                </div>
                                <div className="tw-w-px tw-h-8 tw-bg-gray-200" />
                                <div className="tw-text-center">
                                    <p className="tw-text-lg tw-font-semibold tw-text-red-600">{failed.length}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Failed</p>
                                </div>
                            </div>
                        </div>

                        {/* Created Notes Table */}
                        {created.length > 0 && (
                            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-3 tw-overflow-hidden">
                                <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                                    <Icon icon="mdi:check-circle-outline" className="tw-text-emerald-500 tw-text-sm" />
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Created Notes ({created.length})</h4>
                                </div>
                                <div className="tw-overflow-x-auto">
                                    <table className="tw-w-full tw-text-xs">
                                        <thead>
                                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Note No</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Flat</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Type</th>
                                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Total</th>
                                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Net Payable</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {created.map((n, idx) => (
                                                <tr key={idx} className="tw-border-b tw-border-gray-50">
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-700 tw-font-medium">{n.document_number || '-'}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{n.wing_title}-{n.flat_number}</td>
                                                    <td className="tw-px-3 tw-py-2">
                                                        <span className={`tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${n.note_type === 'CREDIT_NOTE' ? 'tw-bg-emerald-50 tw-text-emerald-600' : 'tw-bg-orange-50 tw-text-orange-600'}`}>
                                                            {n.note_type === 'CREDIT_NOTE' ? 'Credit' : 'Debit'}
                                                        </span>
                                                    </td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{formatCurrency(n.total_amount)}</td>
                                                    <td className={`tw-px-3 tw-py-2 tw-text-right tw-font-medium ${n.note_type === 'CREDIT_NOTE' ? 'tw-text-emerald-600' : 'tw-text-orange-600'}`}>{formatCurrency(n.net_payable)}</td>
                                                    <td className="tw-px-3 tw-py-2">
                                                        <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-gray-100 tw-text-gray-600">
                                                            {n.status || 'DRAFT'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Failed Rows Table */}
                        {failed.length > 0 && (
                            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-3 tw-overflow-hidden">
                                <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                                    <Icon icon="mdi:alert-outline" className="tw-text-red-500 tw-text-sm" />
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Failed Rows ({failed.length})</h4>
                                </div>
                                <div className="tw-overflow-x-auto">
                                    <table className="tw-w-full tw-text-xs">
                                        <thead>
                                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Row</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Flat</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Errors</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {failed.map((f, idx) => (
                                                <tr key={idx} className="tw-border-b tw-border-gray-50 tw-bg-red-50/50">
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-500">{f.row ?? f.rowNum ?? idx + 1}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-700">{f.wing_title && f.flat_number ? `${f.wing_title}-${f.flat_number}` : '-'}</td>
                                                    <td className="tw-px-3 tw-py-2">
                                                        <div className="tw-flex tw-flex-wrap tw-gap-1">
                                                            {(Array.isArray(f.errors) ? f.errors : [f.error || f.message || 'Unknown error']).map((e, i) => (
                                                                <span key={i} className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-medium tw-bg-red-100 tw-text-red-600">
                                                                    {e}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Action */}
                        <div className="tw-flex tw-items-center tw-justify-end tw-mt-4">
                            <button onClick={goBack} className="tw-flex tw-items-center tw-gap-1.5 tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-white tw-bg-indigo-600 hover:tw-bg-indigo-700 tw-transition-colors">
                                <Icon icon="mdi:note-multiple-outline" className="tw-text-sm" />
                                Go to {listLabel}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkUploadCreditDebitNotes;
